import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  TextArea,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  UncontrolledTooltip,
  Table
} from "reactstrap";
import { Formik, useFormik, ErrorMessage, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import { FiXCircle } from "react-icons/fi";
import { FiTrash2 } from "react-icons/fi";
import CustomSelect from "components/Common/custom-select";
import Breadcrumbs from "components/Common/Breadcrumb";
import TableContainer from "components/Common/TableContainer";
import getAccessRights from "components/Common/getAccessRights";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { fetchActiveEmployees, fetchMonthlyDeduction, saveMonthlyDeduction, fetchDeduction, removeMonthlyDeduction, importMonthlyDeduction, getDeductionTemplate } from "store/actions";
import Swal from "sweetalert2";
import Loader from "common/pageLoader";
import DatePicker from "react-datepicker";
import CustomMonthPicker from "common/CustomMonthPicker";

const MonthlyDeductions = () => {
  const formikRef = useRef();

  const [monthlyDeductionDataDefault, setMonthlyDeductionDataDefault] = useState({ emp_id: "", emp_code: "", emp_name: "", amount: "", ded_id: "0", monthly_ded_id: "", emp_no: '', remarks: "", enable_status: false, entry_type: 2 });
  const [monthlyDeductionData, setMonthlyDeductionData] = useState([]);
  const [deleteData, setDeleteData] = useState([]);

  const [searchStatus, setSearchStatus] = useState(false);
  const [saveStatus, setSaveStatus] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [month, setMonth] = useState(null);

  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    monthlyDeduction,
    deductionList,
    employees,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    monthlyDeduction: state.PayrollDeductionMonthlyDeductionReducer?.monthlyDeduction ?? [],
    deductionList: state.PayrollDeductionFixedDeductionReducer?.deductionList ?? [],
    employees: state.EmployeeDetailsReducer?.employeesActiveList ?? [],
    loading: state.PayrollDeductionMonthlyDeductionReducer?.loading,
    error: state.PayrollDeductionMonthlyDeductionReducer?.error,
    success: state.PayrollDeductionMonthlyDeductionReducer?.success,
  }));

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deductionsData, setDeductionsData] = useState(null);
  const [deductionMonth, setDeductionMonth] = useState(null);
  const [employeeData, setEmployeeData] = useState(employees);
  const currentDeductionEmployeeData = useRef({ emp_id: '', emp_no: '', emp_name: '' });
  const [fetchMonthlyDeductionStatus, setFetchMonthlyDeductionStatus] = useState(false);

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const accessRight = getAccessRights(location.pathname, history);
    setMonthlyDeductionData([]);
  }, [location]);

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchActiveEmployees(unitId))
    dispatch(fetchDeduction(unitId, 1))
  }, [dispatch]);

  useEffect(() => {
    if (success) {

      if (fetchMonthlyDeductionStatus) {
        const formData = new FormData();
        formData.append("pay_dedn_id", deductionsData.value);
        formData.append("salary_month", deductionMonth);
        formData.append("create_by", authDetails?.user_id);
        formData.append("unit_id", authDetails?.unit_id);
        dispatch(fetchMonthlyDeduction(formData));
      }
      setFetchMonthlyDeductionStatus(false);

      if (monthlyDeduction?.length > 0) {
        //Remove the selected employees from the list
        resetEmployeeData(monthlyDeduction);

        setMonthlyDeductionData([...monthlyDeduction, monthlyDeductionDataDefault]);
      } else {
        setMonthlyDeductionData([monthlyDeductionDataDefault]);
        resetEmployeeData([monthlyDeductionDataDefault]);
      }
      setIsEdit(false);
      setEditId(null);
    } else {
      setMonthlyDeductionData([monthlyDeductionDataDefault]);
    }

  }, [authDetails, success, dispatch]);

  const initialDCancelRemarksValues = { remarks: "" };

  const validationDCancelRemarksSchema = Yup.object({
    remarks: Yup.string().required("Remarks is required"),
  });

  const initialValues = { deductions: "", deduction_month: null };

  const validationSchema = Yup.object({
    deductions: Yup.object().nullable().required("Deductions is required"),
    deduction_month: Yup.date().required("Deduction month is required").typeError("Deduction month is required"),
  });

  const initialDeductionValues = { emp_no: "", emp_name: "", amount: "", remarks: "" };

  const deductionSchema = Yup.object().shape({
    emp_id: '',
    emp_code: '',
    emp_no: Yup.object().test('emp_no', 'Employee Number is required', function (value) {
      const { path, parent, options } = this;
      const index = parseInt(path.match(/\d+/)?.[0] ?? -1);
      //const lastIndex = options.context?.length - 1;
      const lastIndex = monthlyDeductionData?.length - 1;

      const isLastRow = index === lastIndex;
      const hasAnyValue = !!parent.emp_no || !!parent.emp_name || !!parent.amount || !!parent.remarks;

      if (isLastRow && !hasAnyValue) {
        return true; // ✅ skip validation if empty
      }

      return !!value; // ✅ validate if value is expected
    }).nullable(),

    emp_name: Yup.string().test('emp_name', 'Name is required', function (value) {
      const { path, parent, options } = this;
      const index = parseInt(path.match(/\d+/)?.[0] ?? -1);
      //const lastIndex = options.context?.length - 1;
      const lastIndex = monthlyDeductionData?.length - 1;

      const isLastRow = index === lastIndex;
      const hasAnyValue = !!parent.emp_no || !!parent.emp_name || !!parent.amount || !!parent.remarks;

      if (isLastRow && !hasAnyValue) {
        return true; // ✅ skip validation if empty
      }

      return !!value; // ✅ validate if value is expected
    }),

    amount: Yup.number().test('amount', 'Amount is required', function (value) {
      const { path, parent, options } = this;
      const index = parseInt(path.match(/\d+/)?.[0] ?? -1);
      //const lastIndex = options.context?.length - 1;
      const lastIndex = monthlyDeductionData?.length - 1;

      const isLastRow = index === lastIndex;
      const hasAnyValue = !!parent.emp_no || !!parent.emp_name || !!parent.amount || !!parent.remarks;

      if (isLastRow && !hasAnyValue) {
        return true; // ✅ skip validation if empty
      }

      return value !== undefined && value !== ''; // ✅ validate if filled
    }),

    remarks: Yup.string().test('remarks', 'Remarks is required', function (value) {
      const { path, parent, options } = this;
      const index = parseInt(path.match(/\d+/)?.[0] ?? -1);
      //const lastIndex = options.context?.length - 1;
      const lastIndex = monthlyDeductionData?.length - 1;

      const isLastRow = index === lastIndex;
      const hasAnyValue = !!parent.emp_no || !!parent.emp_name || !!parent.amount || !!parent.remarks;

      if (isLastRow && !hasAnyValue) {
        return true; // ✅ skip validation if empty
      }

      return !!value; // ✅ validate if value is expected
    }),
  });

  const validationDeductionSchema = Yup.object().shape({
    deduction: Yup.array().of(deductionSchema)
  });

  const handleSearch = async (formik) => {
    const errors = await formik.validateForm();
    formik.setTouched({ deductions: true, deduction_month: true });
    if (Object.keys(errors).length > 0) {
      return;
    }

    // await formik.validateField('deductions');
    // formik.setFieldTouched('deductions', true);

    // await formik.validateField('deduction_month');
    // formik.setFieldTouched('deduction_month', true);

    setMonthlyDeductionData([]);

    if (formik?.values?.deductions?.value) {
      const formData = new FormData();
      formData.append("pay_dedn_id", formik.values.deductions.value);
      //formData.append("salary_month", formatDateToYYYYMMDD(formik.values.deduction_month));
      formData.append("salary_month", formik.values.deduction_month);
      formData.append("create_by", authDetails?.user_id);
      formData.append("unit_id", authDetails?.unit_id);
      //dispatch(fetchDeduction(formData));
      dispatch(fetchMonthlyDeduction(formData));

      setSearchStatus(true);
    }
  };

  const handleDelete = (ded, index) => {
    if (ded?.entry_type === 2) {
      const newMonthlyDedData = removeByIndex(monthlyDeductionData, index); // Removes object at index
      setMonthlyDeductionData(newMonthlyDedData);
      //Remove the selected employees from the list
      resetEmployeeData(newMonthlyDedData);
    } else {
      //console.log('ded',ded);
      setDeleteData(ded);
      setIsOpen(true);
    }
  };

  const handleNextRow = (index) => {
    if (formikRef.current) {
      formikRef.current[index]?.focus();
      formikRef.current.validateForm().then((errors) => {
        formikRef.current.handleSubmit();

        if (!errors.deduction) {
          const findEmptyDeduction = formikRef.current.values.deduction.filter(emp => emp.emp_no === '' || emp.emp_name === '' || emp.amount === '' || emp.remarks === '');
          if (findEmptyDeduction.length <= 0) {
            let monthlyDedData = [...formikRef.current.values.deduction, ...[monthlyDeductionDataDefault]];
            monthlyDedData.forEach(function (element) {
              if (element?.emp_no) {
                element.enable_status = true;
                element.emp_no = { value: element.emp_id, label: element.emp_no + ' - ' + element.emp_name };
                //element.ded_id = element.monthly_ded_id;
                element.ded_id = 0;
              }
            });
            setMonthlyDeductionData(monthlyDedData);

            //Remove the selected employees from the list
            resetEmployeeData(monthlyDedData);
            currentDeductionEmployeeData.current = '';

          }
        }
      });
    }
  }

  const handleSave = () => {
    //formik.handleSubmit();
    if (formikRef.current) {
      formikRef.current.validateForm().then((errors) => {
        formikRef.current.handleSubmit();
        if (!errors.deduction) {
          let submitData = formikRef.current.values.deduction;
          submitData = submitData.filter(function (obj) {
            return obj.emp_name !== '';
          });

          const newMonthlyDeductionCount = submitData.filter(entry => entry.entry_type === 2).length;
          if (newMonthlyDeductionCount > 0) {
            const keysToRemove = ['emp_name', 'emp_no', 'enable_status', 'entry_type', 'monthly_ded_id'];

            submitData = submitData.map(obj => {
              const newObj = { ...obj };
              keysToRemove.forEach(key => delete newObj[key]);
              return newObj;
            });

            let lst_employee_deduction_data = { ded: submitData };

            const formData = new FormData();
            formData.append("lst_employee_deduction", JSON.stringify(lst_employee_deduction_data));
            formData.append("ded_type_id", deductionsData?.value);
            formData.append("salary_month", deductionMonth);
            formData.append("create_by", authDetails?.user_id);
            dispatch(saveMonthlyDeduction(formData));
            setFetchMonthlyDeductionStatus(true);
          }
        }
      })
    }
  };

  const removeByIndex = (array, indexToRemove) => { return array.filter((_, index) => index !== indexToRemove); };

  const resetEmployeeData = (monthlyDeduction) => {
    const withoutSelectedEmployees = employees.filter(function (item) {
      return !monthlyDeduction.some(function (fd) {
        return fd.emp_id == item.value;
      });
    });
    setEmployeeData(withoutSelectedEmployees);
  };

  const resetMonthlyDeductionData = () => { setMonthlyDeductionData([]); setSearchStatus(false); };

  const [selectedFile, setSelectedFile] = useState(null);
  const handleGetTemplate = () => {
    // Prepare FormData
    const formData = new FormData();
    formData.append("paydedn_id", deductionsData?.value); // pass month here
    formData.append("unit_id", authDetails?.unit_id);
    formData.append("create_by", authDetails?.user_id);

    // Dispatch Redux action
    dispatch(getDeductionTemplate(formData));
  };
  const handleFileChange = (e) => { setSelectedFile(e.target.files[0]); };
  const handleImport = () => {
    if (!selectedFile) { alert("Please select a file first!"); return; }
    const formData = new FormData();
    formData.append("ded_file", selectedFile);
    formData.append("paydedn_id", deductionsData?.value);
    formData.append("salary_month", deductionMonth);
    formData.append("create_by", authDetails?.user_id);
    formData.append("unit_id", authDetails?.unit_id);
    dispatch(importMonthlyDeduction(formData));
  };

  useEffect(() => {
    if (!success) {
      const formData = new FormData();
      formData.append("pay_dedn_id", deductionsData?.value);
      formData.append("create_by", authDetails?.user_id);
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("salary_month", deductionMonth);
      dispatch(fetchMonthlyDeduction(formData));
      setSearchStatus(true);
    }
  }, [authDetails, success, dispatch]);
  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Payroll / Deduction" breadcrumbItem="Monthly Deductions" />
        <Card>
          <CardBody>

            <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
              <ModalHeader tag="h4">
                Cancel Remarks <span className="text-danger">*</span>
              </ModalHeader>
              <ModalBody>
                <Formik
                  initialValues={initialDCancelRemarksValues}
                  validationSchema={validationDCancelRemarksSchema}
                  onSubmit={(values) => {
                    const formData = new FormData();
                    formData.append("pay_ded_id", deleteData?.pay_ded_id);
                    formData.append("remarks", values?.remarks);
                    formData.append("create_by", authDetails?.user_id);
                    dispatch(removeMonthlyDeduction(formData));
                    setDeleteData();
                    setIsOpen(false);
                    setFetchMonthlyDeductionStatus(true);
                  }}>
                  {(formik) => (
                    <Form>
                      <Row form>
                        <Col xs={12}>
                          <div className="mb-3">
                            <Label className="form-label">Remarks<span className="text-danger">*</span></Label>
                            <Field as="textarea" name="remarks" className="form-control" />
                            <ErrorMessage name="remarks" component="div" className="text-danger" />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <div className="text-end">
                            <Button type="submit" className="btn button-save me-2">
                              <i className="fas fa-save me-1" /> Save
                            </Button>
                            <button
                              type="button"
                              className="btn button-cancel"
                              onClick={() => {
                                Swal.fire({
                                  title: "Are you sure?",
                                  text: "Do you want to cancel this action?",
                                  icon: "warning",
                                  showCancelButton: true,
                                  confirmButtonText: "Yes, Cancel",
                                  cancelButtonText: "No",
                                  confirmButtonColor: "#3085d6",
                                  cancelButtonColor: "#d33",
                                }).then((result) => {
                                  if (result.isConfirmed) {
                                    setIsOpen(false);
                                    setDeleteData();
                                  }
                                });
                              }}
                            >
                              <i className="fas fa-sync me-1" /> Cancel
                            </button>
                            {/* <button type="button" onClick={() => { setIsOpen(false); setDeleteData(); }} className="btn button-cancel" >
                              <i className="fas fa-sync me-1" /> Cancel
                            </button> */}
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </ModalBody>
            </Modal>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                //console.log('Submitted',values);
              }}>
              {(formik) => (
                <Form>
                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Deductions <span className="text-danger">*</span></Label>
                        <CustomSelect
                          id="deductions"
                          name="deductions"
                          options={deductionList}
                          value={deductionsData}
                          onChange={(selectedOption) => {
                            formik.setFieldValue("deductions", selectedOption);
                            setDeductionsData(selectedOption);
                            resetMonthlyDeductionData();
                          }}
                        />
                        {formik.touched.deductions && formik.errors.deductions && (
                          <p className="text-danger ">{formik.errors.deductions}</p>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="month">Month <span className="text-danger">*</span></Label>
                        <CustomMonthPicker
                          id="deduction_month"
                          name="deduction_month"
                          value={formik.values.deduction_month}
                          onChange={(date) => {
                            formik.setFieldValue("deduction_month", date);
                            setDeductionMonth(date);
                            resetMonthlyDeductionData();
                          }}
                          onBlur={() => formik.setFieldTouched("deduction_month", true)}
                        />
                        {formik.touched.deduction_month && formik.errors.deduction_month && (
                          <p className="text-danger ">{formik.errors.deduction_month}</p>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="auto" className="d-flex align-items-end mb-4 g-1">
                      <Button type="button" className="btn button-search me-2" onClick={() => handleSearch(formik)}>
                        <i className="fas fa-search me-1" /> Search
                      </Button>
                    </Col>
                    {searchStatus && (
                      <Col md="auto" className="d-flex align-items-end mb-4 g-1">
                        <Button color="success" className="btn button-save me-2" onClick={handleSave}>
                          <i className="fas fa-save me-1" /> Save
                        </Button>
                      </Col>
                    )}
                    {searchStatus && (
                      <Col md="auto" className="d-flex align-items-end mb-4 g-1">
                        <Button
                          color="secondary"
                          className="btn button-cancel"
                          onClick={() => {
                            Swal.fire({
                              title: "Are you sure?",
                              text: "All unsaved data will be cleared.",
                              icon: "warning",
                              showCancelButton: true,
                              confirmButtonText: "Yes, Cancel",
                              cancelButtonText: "No",
                              confirmButtonColor: "#3085d6",
                              cancelButtonColor: "#d33",
                            }).then((result) => {
                              if (result.isConfirmed) {
                                formik.resetForm();
                                formik.setFieldValue("deductions", null);
                                setMonthlyDeductionData([]);
                                setDeductionsData(null);
                                setSearchStatus(false);
                                setSelectedFile(null);
                              }
                            });
                          }}
                        >
                          <i className="fas fa-sync me-1" /> Cancel
                        </Button>
                      </Col>
                      // <Col md="auto" className="d-flex align-items-end mb-4 g-1">
                      //   <Button
                      //     color="secondary"
                      //     className="btn button-cancel"
                      //     onClick={() => {
                      //       formik.resetForm();
                      //       formik.setFieldValue("deductions", null);
                      //       setMonthlyDeductionData([]);
                      //       setDeductionsData(null);
                      //       setSearchStatus(false);
                      //       setSelectedFile(null);
                      //     }}
                      //   >
                      //     <i className="fas fa-sync me-1" /> Cancel
                      //   </Button>
                      // </Col>
                    )}
                    {searchStatus && (
                      <Col md="auto" className="d-flex align-items-end mb-4">
                        <Button type="button" color="primary" className="button-export" onClick={handleGetTemplate}>
                          <i className="fas fa-download me-1" /> Get Template
                        </Button>
                      </Col>
                    )}
                  </Row>
                  {searchStatus && (
                    <Row className="align-items-end">
                      <Col md={3}>
                        <Label for="file">File <span className="text-danger">*</span></Label>
                        <Input
                          id="file"
                          name="file"
                          type="file"
                          accept=".xls,.xlsx"
                          onChange={handleFileChange}
                          onBlur={formik.handleBlur}
                          className={`form-control ${formik.touched.file && formik.errors.file ? "is-invalid" : ""
                            }`}
                        />
                        {formik.touched.file && formik.errors.file && (
                          <div className="invalid-feedback d-block">{formik.errors.file}</div>
                        )}
                      </Col>
                      <Col md={2}>
                        <Button type="button" color="success" className="button-export" onClick={handleImport} disabled={!selectedFile}>
                          <i className="fas fa-file-import me-1" /> Import
                        </Button>
                      </Col>
                    </Row>
                  )}
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Formik
              initialValues={{ deduction: monthlyDeductionData }}
              enableReinitialize
              innerRef={formikRef}
              validationSchema={validationDeductionSchema}
              //context={{ length: monthlyDeductionData?.length ? monthlyDeductionData?.length:0 }} // Required for row logic
              validateOnBlur={true}
              validateOnChange={true}
              onSubmit={(values) => {
                //console.log('Submitted',values);
              }}>
              {({ values, setFieldValue, setFieldTouched, validateField, errors, touched }) => (
                <Form>
                  <div className="table-responsive overflow-visible">
                    <Table bordered>
                      <thead className="thead-dark">
                        <tr>
                          <th className="text-center">Action</th>
                          <th>Emp Code<span className="text-danger ms-1">*</span></th>
                          <th>Name</th>
                          <th>Amount<span className="text-danger ms-1">*</span></th>
                          <th>Remarks<span className="text-danger ms-1">*</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        <FieldArray name="deduction">
                          {() => (
                            typeof deductionsData === 'object' && deductionsData !== null && values?.deduction?.length > 0 ? (
                              values.deduction.map((ded, index) => (
                                <tr key={index}>
                                  <td className="text-center">
                                    {(monthlyDeductionData?.length > 1 && values?.deduction?.length - 1 != index) && (
                                      <FiTrash2 size={20} color="red" style={{ cursor: "pointer" }} onClick={() => handleDelete(ded, index)} />)}
                                  </td>
                                  <td>
                                    {
                                      ded.enable_status ?
                                        <>
                                          <Input name={`deduction[${index}].emp_code`} disabled value={ded.emp_code} />
                                          <Input type="hidden" name={`deduction[${index}].emp_no`} disabled value={ded.emp_no} />
                                        </>
                                        :
                                        <>
                                          <CustomSelect
                                            options={employeeData}
                                            isDisabled={ded.enable_status}
                                            name={`deduction[${index}].emp_no`}
                                            value={employeeData.find((option) => option.value == ded.emp_id)} // Store full object
                                            onChange={(selectedOption) => {
                                              if (!selectedOption) {
                                                setFieldValue(`deduction[${index}].emp_no`, null);
                                                setFieldValue(`deduction[${index}].emp_id`, "");
                                                setFieldValue(`deduction[${index}].emp_code`, "");
                                                setFieldValue(`deduction[${index}].emp_name`, "");
                                                return;
                                              }
                                              const [emp_code, emp_name] = selectedOption.label.split(" - ");

                                              setFieldValue(`deduction[${index}].emp_no`, selectedOption);
                                              setFieldTouched(`deduction[${index}].emp_no`, true, true);
                                              validateField(`deduction[${index}].emp_no`);

                                              setFieldValue(`deduction[${index}].emp_id`, selectedOption.value);
                                              setFieldTouched(`deduction[${index}].emp_id`, true, true);

                                              setFieldValue(`deduction[${index}].emp_code`, emp_code);
                                              setFieldTouched(`deduction[${index}].emp_code`, true, true);

                                              setFieldValue(`deduction[${index}].emp_name`, emp_name);
                                              setFieldTouched(`deduction[${index}].emp_name`, true, true);
                                              validateField(`deduction[${index}].emp_name`);

                                              //console.log('-----------',selectedOption);
                                              //currentDeductionEmployeeData.current.emp_id = selectedOption.value;
                                              //currentDeductionEmployeeData.current.emp_no = selectedOption;
                                              //currentDeductionEmployeeData.current.emp_name = emp_name;

                                              //formikRef.current.values.deduction[index].emp_no = selectedOption;
                                              //formikRef.current.values.deduction[index].emp_id = selectedOption.value;
                                              //formikRef.current.values.deduction[index].emp_name = emp_name;

                                              handleNextRow(index);
                                            }
                                            }
                                            onBlur={() => {
                                              setFieldTouched(`deduction[${index}].emp_name`, true);
                                              setFieldTouched(`deduction[${index}].emp_no`, true);
                                              handleNextRow(index);
                                            }
                                            }
                                          />
                                          {
                                            /*
                                            onChange={(selectedOption) => { 
                                                console.log(selectedOption);
                                                let selectedEmpArray = selectedOption.label.split(" - ");
                                                if(selectedEmpArray?.length>1)
                                                {
                                                  setFieldValue(`deduction[${index}].emp_name`, selectedEmpArray[1]);
                                                  setFieldTouched(`deduction[${index}].emp_name`, true, true);
                                                  validateField(`deduction[${index}].emp_name`);
        
                                                  setFieldValue(`deduction[${index}].emp_id`, selectedOption.value);
                                                  setFieldTouched(`deduction[${index}].emp_id`, true, true);
                                                  //validateField(`deduction[${index}].emp_id`);
        
                                                  setFieldValue(`deduction[${index}].emp_code`, selectedEmpArray[0]);
                                                  setFieldTouched(`deduction[${index}].emp_code`, true, true);
                                                  
                                                }                                      
                                                setFieldValue(`deduction[${index}].emp_no`, selectedOption);
                                                setFieldTouched(`deduction[${index}].emp_no`, true, true);
                                                validateField(`deduction[${index}].emp_no`);
        
                                                setFieldTouched(`deduction[${index}].amount`, true, true);
                                                validateField(`deduction[${index}].amount`);
                                                validateField(`deduction[${index}].remarks`);                                        
                                              }
                                            
                                            */
                                          }
                                          <ErrorMessage name={`deduction[${index}].emp_no`} component="div" className="error" />
                                        </>
                                    }
                                  </td>
                                  <td>
                                    <Input name={`deduction[${index}].emp_name`} disabled value={ded.emp_name} onChange={(e) => { handleNextRow(index); }} onBlur={(e) => { handleNextRow(index); }} />
                                    <ErrorMessage name={`deduction[${index}].emp_name`} component="div" className="error" />
                                  </td>
                                  <td>
                                    <Input name={`deduction[${index}].amount`} type="number" className="text-end1" disabled={ded.enable_status} value={ded.amount}
                                      // onInput={
                                      //   (e) => {
                                      //     setFieldValue(`deduction[${index}].amount`, e.target.value);
                                      //     //handleNextRow(index);
                                      //   }
                                      // }
                                      onKeyDown={(e) => {
                                        if (["-", "+", "*", "/", "e", "E", "."].includes(e.key)) {
                                          e.preventDefault();
                                        }
                                      }}
                                      onInput={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, "");
                                        setFieldValue(`deduction[${index}].amount`, value);
                                      }}
                                      onBlur={(e) => { handleNextRow(index); }}
                                    />
                                    <ErrorMessage name={`deduction[${index}].amount`} component="div" className="error" />
                                  </td>
                                  <td>
                                    <Input name={`deduction[${index}].remarks`} value={ded.remarks} className="text-end1" disabled={ded.enable_status} onInput={
                                      (e) => {
                                        setFieldValue(`deduction[${index}].remarks`, e.target.value);
                                        //handleNextRow(index);                                    
                                      }
                                    }
                                      onBlur={(e) => { handleNextRow(index); }}
                                    />
                                    <ErrorMessage name={`deduction[${index}].remarks`} component="div" className="error" />
                                  </td>
                                </tr>
                              ))
                            ) : '')
                          }
                        </FieldArray>
                      </tbody>
                    </Table>
                  </div>
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default MonthlyDeductions;
